/**
 * SlideCart upsell click compatibility.
 *
 * SlideCart can leave rendered advanced-upsell buttons without a working click
 * path after its drawer has been refreshed by third-party cart hooks. Handle
 * only the app's upsell controls and hand the resulting cart back to SlideCart.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '#slidecarthq';
  const UPSELL_SELECTOR = '[data-testid="Aupsells"] .upsell';
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';
  const productCache = new Map();

  function getRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;
  }

  function getProductHandle(container) {
    const href = container?.querySelector(PRODUCT_LINK_SELECTOR)?.getAttribute('href');
    if (!href) return '';

    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  }

  async function getProduct(handle) {
    if (!handle) throw new Error('Could not identify the upsell product.');
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(getRoute(`products/${encodeURIComponent(handle)}.js`), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Could not load the upsell product (${response.status}).`);
      return response.json();
    }).catch((error) => {
      productCache.delete(handle);
      throw error;
    });

    productCache.set(handle, request);
    return request;
  }

  function getSelectedVariant(product, selectedOptions = []) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!selectedOptions.length) return variants.find((variant) => variant.available) || null;

    return variants.find((variant) => {
      if (!variant.available) return false;
      return selectedOptions.every((value, index) => String(variant.options?.[index]) === String(value));
    }) || null;
  }

  function setButtonLoading(button, loading) {
    if (!button) return;

    if (loading) {
      button.dataset.slideCartUpsellOriginalText = button.textContent || '';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = 'Adding…';
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-disabled');
    if (button.dataset.slideCartUpsellOriginalText) {
      button.textContent = button.dataset.slideCartUpsellOriginalText;
      delete button.dataset.slideCartUpsellOriginalText;
    }
  }

  function showError(button, message) {
    const parent = button?.parentElement;
    if (!parent) return;

    parent.querySelector('.slidecart-upsell-error')?.remove();
    const error = document.createElement('p');
    error.className = 'slidecart-upsell-error';
    error.setAttribute('role', 'alert');
    error.textContent = message || 'This item could not be added. Please try again.';
    parent.appendChild(error);
  }

  async function addVariant(button, variantId) {
    if (!variantId || button.dataset.slideCartUpsellAdding === 'true') return;

    button.dataset.slideCartUpsellAdding = 'true';
    setButtonLoading(button, true);

    try {
      const addResponse = await fetch(getRoute('cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'This item could not be added.');
      }

      const cartResponse = await fetch(getRoute('cart.js'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!cartResponse.ok) throw new Error('The cart could not be refreshed.');
      const cart = await cartResponse.json();

      if (typeof window.SLIDECART_SET_CART === 'function') {
        window.SLIDECART_SET_CART(cart);

        // SLIDECART_SET_CART only replaces SlideCart's internal cart state; it
        // does not run the lifecycle callback used by our gift-wrap enhancer.
        // Invoke it explicitly so the enhancer refreshes its cache and restores
        // the per-line gift-wrapping controls after SlideCart re-renders.
        if (typeof window.SLIDECART_UPDATED === 'function') {
          await window.SLIDECART_UPDATED(cart);
        }
      } else if (typeof window.SLIDECART_UPDATE === 'function') {
        // SlideCart treats this argument as a callback, not as cart data.
        window.SLIDECART_UPDATE();
      }

      if (typeof window.SLIDECART_UPSELL_ADD === 'function') {
        window.SLIDECART_UPSELL_ADD(Number(variantId));
      }
      return true;
    } catch (error) {
      console.warn('[SlideCart upsell] Add failed', error);
      showError(button, error.message);
      setButtonLoading(button, false);
      delete button.dataset.slideCartUpsellAdding;
      return false;
    }
  }

  function findOptionsParts(productId) {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || !productId) return {};

    const overlay = Array.from(root.querySelectorAll('.upsell-options-overlay'))
      .find((element) => String(element.dataset.slidecartProductId) === String(productId));
    const dialog = overlay?.nextElementSibling?.classList.contains('upsell-options')
      ? overlay.nextElementSibling
      : null;

    return { overlay, dialog };
  }

  function openOptions(card) {
    const productId = card?.dataset.slidecartProductId;
    const { overlay, dialog } = findOptionsParts(productId);
    if (!overlay || !dialog) return false;

    window.GIFTWRAP_PRELOAD_ELIGIBILITY?.(getProductHandle(card));

    overlay.classList.add('active');
    dialog.classList.add('active');
    dialog.removeAttribute('aria-hidden');
    dialog.removeAttribute('inert');
    dialog.focus({ preventScroll: true });
    return true;
  }

  function closeOptions(dialog) {
    if (!dialog) return;
    const overlay = dialog.previousElementSibling?.classList.contains('upsell-options-overlay')
      ? dialog.previousElementSibling
      : null;

    overlay?.classList.remove('active');
    dialog.classList.remove('active');
    dialog.setAttribute('aria-hidden', 'true');
    dialog.setAttribute('inert', '');
  }

  async function addSimpleUpsell(button, card) {
    try {
      const handle = getProductHandle(card);
      window.GIFTWRAP_PRELOAD_ELIGIBILITY?.(handle);
      const product = await getProduct(handle);
      const variant = getSelectedVariant(product);
      if (!variant) throw new Error('This item is currently sold out.');
      await addVariant(button, variant.id);
    } catch (error) {
      console.warn('[SlideCart upsell] Product resolution failed', error);
      showError(button, error.message);
      setButtonLoading(button, false);
      delete button.dataset.slideCartUpsellAdding;
    }
  }

  async function addOptionsUpsell(button, dialog) {
    try {
      const handle = getProductHandle(dialog);
      window.GIFTWRAP_PRELOAD_ELIGIBILITY?.(handle);
      const product = await getProduct(handle);
      const selectedOptions = Array.from(dialog.querySelectorAll('select')).map((select) => select.value);
      const variant = getSelectedVariant(product, selectedOptions);
      if (!variant) throw new Error('The selected variant is currently sold out.');
      const added = await addVariant(button, variant.id);
      if (added) closeOptions(dialog);
    } catch (error) {
      console.warn('[SlideCart upsell] Variant resolution failed', error);
      showError(button, error.message);
      setButtonLoading(button, false);
      delete button.dataset.slideCartUpsellAdding;
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = target.closest(ROOT_SELECTOR);
    if (!root) return;

    const optionsDialog = target.closest('.upsell-options');
    const optionsAddButton = target.closest('.upsell-options button.button.full');
    if (optionsAddButton && optionsDialog) {
      event.preventDefault();
      event.stopImmediatePropagation();
      addOptionsUpsell(optionsAddButton, optionsDialog);
      return;
    }

    const closeButton = target.closest('.upsell-options-close');
    if (closeButton && optionsDialog) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeOptions(optionsDialog);
      return;
    }

    const optionsOverlay = target.closest('.upsell-options-overlay.active');
    if (optionsOverlay) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const dialog = optionsOverlay.nextElementSibling?.classList.contains('upsell-options')
        ? optionsOverlay.nextElementSibling
        : null;
      closeOptions(dialog);
      return;
    }

    const addButton = target.closest(`${UPSELL_SELECTOR} .upsell-add button`);
    const card = addButton?.closest('.upsell');
    if (!addButton || !card) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (card.classList.contains('multi')) {
      if (!openOptions(card)) showError(addButton, 'Options could not be opened.');
      return;
    }

    addSimpleUpsell(addButton, card);
  }, true);
})();
